import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useTextSnippetStore } from '../../store/useTextSnippetStore';
import { renderInvoiceSnippet, renderOfferSnippet } from '../../api/textSnippets';
import { useSnippetPreview } from '../../hooks/useSnippetPreview';
import { TextArea } from './TextArea';
import { SnippetPreviewPanel } from './SnippetPreviewPanel';
import {
  type InvoiceSnippetCategory,
  type OfferSnippetCategory,
  type RenderInvoiceSnippetRequest,
  type RenderOfferSnippetRequest,
} from '../../types/textSnippets';

export type OfferSnippetContext = Omit<RenderOfferSnippetRequest, 'body'>;
export type InvoiceSnippetContext = Omit<RenderInvoiceSnippetRequest, 'body'>;

interface SnippetTextAreaBaseProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  error?: string;
  maxLength?: number;
  disabled?: boolean;
  /** Live preview panel under the field (default on). */
  showPreview?: boolean;
}

// why: category, documentType and context are one discriminated union, so an invoice
// category can never be paired with the offer render endpoint (compile error, not a warning).
export type SnippetTextAreaProps = SnippetTextAreaBaseProps &
  (
    | { documentType: 'offer'; category: OfferSnippetCategory; renderContext: OfferSnippetContext }
    | { documentType: 'invoice'; category: InvoiceSnippetCategory; renderContext: InvoiceSnippetContext }
  );

/**
 * Text field with a snippet picker and a debounced live preview (ADR-0020). Picking a
 * snippet inserts its raw body — placeholders stay visible and editable; the preview shows
 * what the backend will resolve, and the backend resolves for real on save.
 */
export function SnippetTextArea(props: SnippetTextAreaProps) {
  const {
    label,
    name,
    value,
    onChange,
    category,
    rows = 3,
    placeholder,
    required,
    error,
    maxLength,
    disabled,
    showPreview = true,
  } = props;
  const { t } = useTranslation(['textSnippets']);
  const allItems = useTextSnippetStore((s) => s.items);
  // why: bumped on every snippet pick so the preview drops the previous snippet's resolution.
  const [pickCount, setPickCount] = useState(0);

  const snippets = useMemo(
    () =>
      allItems
        .filter((item) => item.category === category && item.isActive)
        .sort((a, b) => {
          if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
          return a.sortOrder - b.sortOrder;
        }),
    [allItems, category]
  );

  const defaultSnippet = useMemo(
    () => snippets.find((s) => s.isDefault),
    [snippets]
  );

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handlePick = (snippetId: string) => {
    const snippet = snippets.find((s) => s.id === snippetId);
    setOpen(false);
    if (!snippet) return;
    // why: insert the raw body — the preview shows the resolution, the backend resolves
    // with the final data on save (FACF-100: no context gets baked in at pick time).
    setPickCount((n) => n + 1);
    onChange(snippet.body);
  };

  // why: the discriminated union is narrowed on props, not on destructured fields, so the
  // request type matches the endpoint for each branch.
  const render = useCallback(
    async (body: string, context: OfferSnippetContext | InvoiceSnippetContext) => {
      if (props.documentType === 'offer') {
        const response = await renderOfferSnippet({ body, ...(context as OfferSnippetContext) });
        return response.renderedBody;
      }
      const response = await renderInvoiceSnippet({ body, ...(context as InvoiceSnippetContext) });
      return response.renderedBody;
    },
    [props.documentType]
  );

  const preview = useSnippetPreview<OfferSnippetContext | InvoiceSnippetContext>({
    body: value,
    context: props.renderContext,
    render,
    enabled: showPreview,
    resetKey: pickCount,
  });

  const showAutoApplyHint = value === '' && !!defaultSnippet;

  const picker =
    snippets.length > 0 ? (
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          disabled={disabled}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-[#00A871] hover:bg-emerald-50 px-2 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FileText className="w-3.5 h-3.5" strokeWidth={2} />
          {t('textSnippets:actions.chooseSnippet')}
          <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
        {open && (
          <div
            className="absolute right-0 z-30 mt-1 w-72 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-md"
            style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
          >
            <ul className="py-1">
              {snippets.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(s.id)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-slate-900 truncate">
                        {s.name}
                      </span>
                      {s.isDefault && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                          {t('textSnippets:badges.default')}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">
                      {s.body.replace(/\s+/g, ' ').slice(0, 80)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    ) : null;

  return (
    <div className="space-y-2" ref={containerRef}>
      <TextArea
        label={label}
        labelAddon={picker}
        name={name}
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        error={error}
        maxLength={maxLength}
        disabled={disabled}
        helperText={
          showAutoApplyHint
            ? t('textSnippets:messages.autoApplyHint', { name: defaultSnippet!.name })
            : undefined
        }
      />

      {preview.active && (
        <SnippetPreviewPanel
          text={preview.text}
          loading={preview.loading}
          hint={t('textSnippets:preview.liveHint')}
        />
      )}
    </div>
  );
}

export default SnippetTextArea;
